'use client';

import { itemsClient } from '@/app/clients/items.client';
import { ordersClient } from '@/app/clients/orders.client';
import ItemCard, { ItemCardSkeleton } from '@/components/cards/item-card';
import { useCart } from '@/contexts/cart.context';
import { useSession } from '@/contexts/session.context';
import { QUERY_PARAMS } from '@/helper/app-constants';
import { ItemModel } from '@/models/item.model';
import { useSearchParams } from 'next/navigation';
import React, { Fragment, Suspense, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

function Page() {
  const [categories, setCategories] = useState<Record<string, ItemModel[]>>({});
  const [selected, setSelected] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const { state, dispatch, loadOrderFromStorage } = useCart();
  const searchParams = useSearchParams();
  const { user, sessionInitialized } = useSession();
  const [fetchingOrder, setFetchingOrder] = useState<boolean | undefined>(
    undefined
  );
  const initRef = useRef(false);
  useEffect(() => {
    itemsClient.list().then((res) => {
      const newCategories: Record<string, ItemModel[]> = {};
      res.data.forEach((item) => {
        if (newCategories[item.category]) {
          newCategories[item.category].push(item);
        } else {
          newCategories[item.category] = [item];
        }
      });
      setCategories(newCategories);
      setSelected(Object.keys(newCategories)[0]);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!initRef.current) {
      if (
        state.items.length !== 0 ||
        !sessionInitialized ||
        fetchingOrder !== undefined
      )
        return; // Dont need to fetch order
      const fetchOrder = async (
        orderId: string | null,
        receipt: string | null,
        isAdmin: boolean | undefined
      ) => {
        // Only admin can access this page with orderId and edit it
        if (orderId && isAdmin) {
          const toastId = toast.loading('Fetching order');
          try {
            setFetchingOrder(true);
            dispatch({ type: 'LOADING_START', storage: false });
            const res = await ordersClient.get(orderId, receipt ?? undefined);

            dispatch({
              type: 'ADD_ITEM_BATCH',
              payload: res.data.items.map((x: any) => ({
                item: x,
                quantity: x.quantity,
              })),
              storage: false,
            });
          } catch (error) {
            toast.error('Failed to fetch order, try to refresh.');
          } finally {
            toast.dismiss(toastId);
            setFetchingOrder(false);
            dispatch({ type: 'LOADING_END', storage: false });
          }
        } else {
          loadOrderFromStorage();
        }
      };

      fetchOrder(
        searchParams.get(QUERY_PARAMS.ORDER_ID),
        searchParams.get('receipt'),
        user?.admin
      );
      initRef.current = true;
    }
  }, [
    searchParams,
    state.items.length,
    dispatch,
    user?.admin,
    loadOrderFromStorage,
    sessionInitialized,
    fetchingOrder,
  ]);

  return (
    <div className="flex flex-col gap-8 overflow-auto h-full">
      <div className="overflow-x-auto pb-2">
        <div role="tablist" className="tabs tabs-border flex whitespace-nowrap">
          {loading ? (
            <div className="skeleton w-full h-6"></div>
          ) : (
            Object.keys(categories).map((category) => (
              <Fragment key={category}>
                <a
                  role="tab"
                  className={`tab first-letter:uppercase ${
                    selected === category ? 'text-primary tab-active' : ''
                  } mb-4`}
                  aria-label={category}
                  defaultChecked={selected === category}
                  onClick={() => setSelected(category)}
                >
                  {category}
                </a>
              </Fragment>
            ))
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 px-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {loading || fetchingOrder
          ? [...Array(16)].map((_, i) => <ItemCardSkeleton key={i} />)
          : categories[selected]?.map((item) => (
              <ItemCard key={item._id} item={item} mode="list" />
            ))}
      </div>
    </div>
  );
}

export default function ServerPage() {
  return (
    <Suspense fallback={<div className="loading loading-spinner"></div>}>
      <Page />
    </Suspense>
  );
}
